/**
 * The app-wide pill shown while files a send took with it are still
 * uploading in the background (#4844). Pure: jobs and live bytes in, the
 * pill's state out, or null when there is nothing to show.
 */

export type BackgroundUploadIndicatorJob = {
  uploadId: string;
  orgThreadId: string;
  /** The task's route id (`ot_…`), when known: what its URL shows. */
  routeRef?: string;
  orgSlug: string;
  sizeBytes: number;
  state: 'uploading' | 'failed';
  /** Stored; only the server's record is still owed. */
  saving?: boolean;
};

export type BackgroundUploadIndicatorState = {
  /** `finishing`: every byte is sent; the server is saving the files. */
  status: 'uploading' | 'finishing' | 'needs_attention';
  /** Files the pill counts: uploading ones, or failed ones needing a hand. */
  count: number;
  loadedBytes: number;
  totalBytes: number;
  /**
   * The task to open; a failed upload's task comes first. `threadRef` is
   * the route segment to link to: the route id when known.
   */
  target: { orgSlug: string; orgThreadId: string; threadRef: string };
};

/** Whether the job belongs to the thread whose route segment is `ref`. */
function isOnThread(job: BackgroundUploadIndicatorJob, ref: string | null) {
  if (!ref) return false;
  return job.routeRef === ref || job.orgThreadId === ref;
}

export function getBackgroundUploadIndicatorState(input: {
  jobs: readonly BackgroundUploadIndicatorJob[];
  /** Live bytes sent by this device, by upload id. */
  loadedBytes?: Readonly<Record<string, number | undefined>>;
  /**
   * The route segment of the thread on screen (`ot_…` or a thread id); its
   * own waiting message already shows this.
   */
  currentThreadRef?: string | null;
}): BackgroundUploadIndicatorState | null {
  // A job whose task isn't created yet belongs to the send that just
  // routed here; it has nowhere to link and its own page shows it.
  const jobs = input.jobs.filter(
    (job) =>
      Boolean(job.routeRef || job.orgThreadId) &&
      !isOnThread(job, input.currentThreadRef ?? null)
  );
  if (jobs.length === 0) return null;
  const failed = jobs.filter((job) => job.state === 'failed');
  const uploading = jobs.filter((job) => job.state === 'uploading');
  const first = failed[0] ?? jobs[0]!;
  let loadedBytes = 0;
  let totalBytes = 0;
  let allSent = uploading.length > 0;
  for (const job of uploading) {
    const size = Math.max(job.sizeBytes, 0);
    const loaded = job.saving
      ? size
      : Math.min(Math.max(input.loadedBytes?.[job.uploadId] ?? 0, 0), size);
    totalBytes += size;
    loadedBytes += loaded;
    // An empty file has nothing to send; it only waits to be saved.
    if (!job.saving && loaded < size) allSent = false;
  }
  return {
    status:
      failed.length > 0
        ? 'needs_attention'
        : allSent
          ? 'finishing'
          : 'uploading',
    count: failed.length > 0 ? failed.length : uploading.length,
    loadedBytes,
    totalBytes,
    target: {
      orgSlug: first.orgSlug,
      orgThreadId: first.orgThreadId,
      threadRef: first.routeRef || first.orgThreadId,
    },
  };
}
